/**
 * sphereArchetypesApi.ts
 *
 * graph-service reads and writes for the Archetypes assigned to one sphere
 * (`GET|PUT /spheres/:id/archetypes`, `GET /spheres/:id/archetypes/available`).
 * Expected outcomes of the write (409 stale revision, 422 per-row problems, 403 not
 * allowed, and any failure whose body says the change was `recorded`) resolve to a typed
 * outcome rather than throwing — the caller renders them. Anything else rethrows for the
 * caller's generic error handling.
 */

import type { ApiClient } from '@center-technologies/shared-api-clients';
import type { EffectiveExpectedAspect, ExpectedAspectsEvaluation } from 'types/archetype';

export interface ArchetypeAssignment {
  systemName: string;
  displayName: string;
  description: string | null;
  isPrimary: boolean;
  /** False when the Archetype was archived, unpublished or is no longer viewable. */
  resolved: boolean;
  applicable: boolean;
  /** `'schema-default'` for Archetypes the sphere's schema chain suggests; `'assigned'` for explicit entries. */
  origin: 'assigned' | 'schema-default';
  /** The schema `system_name` that declared this default. Present only when `origin` is `'schema-default'`. */
  inheritedFrom?: string;
}

export interface SphereArchetypesRead {
  archetypes: ArchetypeAssignment[];
  revision: string | null;
  /** From the server's own authority check — never re-derived in the client. */
  canAssign: boolean;
  /**
   * Who the authority belongs to, so a read-only row can say so: `self` = only the person this
   * sphere is, `edit` = anyone who can edit the sphere. Absent on an older server, and absent
   * whenever `assignGateUnavailable` is set.
   */
  assignGate?: 'self' | 'edit';
  /**
   * True when the server could not determine the authority (SpiceDB or the sphere read did not
   * answer): `canAssign` is false, but that is not a denial, so the client must not say who can
   * change the set.
   */
  assignGateUnavailable?: boolean;
  /** Expected aspects from assigned Archetypes, with presence evaluation. */
  effective?: {
    expectedAspects: EffectiveExpectedAspect[];
    expectedAspectsEvaluation?: ExpectedAspectsEvaluation;
  };
}

export interface AvailableArchetype {
  systemName: string;
  displayName: string;
  description?: string | null;
  glyph?: string | null;
  glyphColor?: string | null;
  suggestsApproaches: string[];
  /** `system_name` of the assigned Archetype that excludes this one; present only when it cannot be added. */
  excludedBy?: string;
}

export type ArchetypeProblemReason =
  | 'unknown'
  | 'unpublished'
  | 'archived'
  | 'not_viewable'
  | 'not_applicable'
  | 'excluded_by'
  | 'is_schema_default';

export interface ArchetypeRowProblem {
  systemName: string;
  reason: ArchetypeProblemReason;
  detail?: string;
}

/** The whole set the user picked: the names, and which one is primary (null only when the set is empty). */
export interface ArchetypeSelection {
  archetypes: string[];
  primary: string | null;
}

export interface PutArchetypesBody {
  archetypes: string[];
  primary: string | null;
  baseRevision?: string | null;
}

export type PutArchetypesOutcome =
  | { kind: 'saved'; value: SphereArchetypesRead | null }
  | { kind: 'conflict'; value: SphereArchetypesRead }
  | { kind: 'rejected'; problems: ArchetypeRowProblem[] }
  | { kind: 'forbidden' }
  /** The set was written, then the answer failed (`recorded: true` on a 403/500/503): the caller re-reads. */
  | { kind: 'recorded' };

interface HttpErrorLike {
  response?: { status?: number; data?: unknown };
  status?: number;
}

function statusOf(err: unknown): number | undefined {
  const e = err as HttpErrorLike | undefined;
  return e?.response?.status ?? e?.status;
}

function bodyOf(err: unknown): Record<string, unknown> {
  const data = (err as HttpErrorLike | undefined)?.response?.data;
  return data && typeof data === 'object' ? (data as Record<string, unknown>) : {};
}

const archetypesPath = (sphereId: string) => `/spheres/${encodeURIComponent(sphereId)}/archetypes`;

export function getSphereArchetypes(graphClient: ApiClient, sphereId: string): Promise<SphereArchetypesRead> {
  return graphClient.get<SphereArchetypesRead>(archetypesPath(sphereId));
}

export async function listAvailableArchetypes(
  graphClient: ApiClient,
  sphereId: string,
  search?: string
): Promise<AvailableArchetype[]> {
  const result = await graphClient.get<{ items: AvailableArchetype[] }>(`${archetypesPath(sphereId)}/available`, {
    params: { limit: 200, ...(search ? { search } : {}) },
  });
  return result.items ?? [];
}

/**
 * Replaces the sphere's whole Archetype set. A 207 (set written, org-type mirror failed) is a
 * success from the user's point of view — the body carries no set, so `value` is null and the
 * caller re-reads.
 */
export async function putSphereArchetypes(
  graphClient: ApiClient,
  sphereId: string,
  body: PutArchetypesBody
): Promise<PutArchetypesOutcome> {
  try {
    const result = await graphClient.put<SphereArchetypesRead | { archetypes: string }>(archetypesPath(sphereId), body);
    return { kind: 'saved', value: Array.isArray((result as SphereArchetypesRead).archetypes) ? (result as SphereArchetypesRead) : null };
  } catch (err) {
    const status = statusOf(err);
    const data = bodyOf(err);
    // Checked before the status: a body sent after the Archetype entry committed carries
    // `recorded: true` whatever its status, and that change landed.
    if (data['recorded'] === true) return { kind: 'recorded' };
    if (status === 409) return { kind: 'conflict', value: data as unknown as SphereArchetypesRead };
    if (status === 422) return { kind: 'rejected', problems: (data['problems'] as ArchetypeRowProblem[]) ?? [] };
    if (status === 403) return { kind: 'forbidden' };
    throw err;
  }
}
